import React, { useState, useEffect, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import { messagesApi } from '../services/api';
import type { Message } from '../types';
import { showToast } from 'shared-ui';

const ConversationDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [messages, setMessages] = useState<Message[]>([]);
  const [businessName, setBusinessName] = useState<string>('');
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (id) loadMessages();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- loadMessages depends on id, stable
  }, [id]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const loadMessages = async () => {
    if (!id) return;
    try {
      setLoading(true);
      setError(null);
      const res = await messagesApi.getMessages(id);
      const list = res?.data ?? [];
      setMessages(Array.isArray(list) ? list : []);
      if (res?.conversation?.businessName) setBusinessName(res.conversation.businessName);
    } catch (err) {
      console.error('Load messages failed:', err);
      setError('Failed to load conversation');
      setMessages([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSend = async () => {
    if (!id || !newMessage.trim() || sending) return;
    setSending(true);
    try {
      const res = await messagesApi.sendMessage(id, newMessage.trim());
      if (res?.success) {
        setNewMessage('');
        loadMessages();
      } else {
        showToast(res?.error || 'Failed to send', 'error');
      }
    } catch (err) {
      console.error('Send failed:', err);
      showToast('Failed to send message', 'error');
    } finally {
      setSending(false);
    }
  };

  const formatTime = (dateString: string) => {
    const d = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - d.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);
    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return d.toLocaleDateString();
  };

  if (!id) {
    return (
      <div>
        <Link to="/messages" style={{ color: '#667eea', fontSize: 14, marginBottom: 16, display: 'inline-block' }}>← Back to Messages</Link>
        <p style={{ color: '#666' }}>No conversation selected</p>
      </div>
    );
  }

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: 60 }}>
        <div style={{ fontSize: 32 }}>💬</div>
        <p style={{ color: '#666', marginTop: 12 }}>Loading conversation...</p>
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: 'calc(100vh - 160px)' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 16, padding: '16px 0', borderBottom: '1px solid #eee' }}>
        <Link to="/messages" style={{ color: '#667eea', fontSize: 14 }}>← Back</Link>
        <h1 style={{ margin: 0, fontSize: 20, color: '#333' }}>{businessName || 'Conversation'}</h1>
      </div>

      {error && (
        <div style={{ padding: 16, background: '#fef2f2', color: '#b91c1c', borderRadius: 10, marginBottom: 16 }}>
          {error}
        </div>
      )}

      <div style={{ flex: 1, overflowY: 'auto', padding: 20, display: 'flex', flexDirection: 'column', gap: 12 }}>
        {messages.map((msg) => (
          <div
            key={msg.id}
            style={{
              alignSelf: msg.senderType === 'customer' ? 'flex-end' : 'flex-start',
              maxWidth: '75%',
              padding: '12px 16px',
              borderRadius: 12,
              background: msg.senderType === 'customer' ? 'linear-gradient(135deg, #667eea, #764ba2)' : '#f0f0f0',
              color: msg.senderType === 'customer' ? 'white' : '#333',
            }}
          >
            <p style={{ margin: 0, fontSize: 14, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{msg.content}</p>
            <p style={{ margin: '6px 0 0 0', fontSize: 11, opacity: 0.8 }}>{formatTime(msg.createdAt)}</p>
          </div>
        ))}
        <div ref={messagesEndRef} />
      </div>

      <div style={{ padding: 16, borderTop: '1px solid #eee', display: 'flex', gap: 12 }}>
        <input
          type="text"
          value={newMessage}
          onChange={(e) => setNewMessage(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && !e.shiftKey && handleSend()}
          placeholder="Type a message..."
          style={{
            flex: 1,
            padding: '12px 16px',
            border: '1px solid #e0e0e0',
            borderRadius: 10,
            fontSize: 14,
          }}
        />
        <button
          type="button"
          onClick={handleSend}
          disabled={sending || !newMessage.trim()}
          style={{
            padding: '12px 24px',
            background: sending || !newMessage.trim() ? '#ccc' : 'linear-gradient(135deg, #667eea, #764ba2)',
            color: 'white',
            border: 'none',
            borderRadius: 10,
            fontWeight: 600,
            cursor: sending || !newMessage.trim() ? 'not-allowed' : 'pointer',
          }}
        >
          {sending ? 'Sending...' : 'Send'}
        </button>
      </div>
    </div>
  );
};

export default ConversationDetail;
